import { getERPClient } from "@/lib/erp/client";

const API = "zaviago_backend.storefront.marketplace_listings";

export interface MarketplaceListing {
  name: string;
  channel: "TikTok Shop" | "Lazada" | "Shopee";
  channel_id: string;
  listing_title: string;
  status: string;
  price: number;
  seller_sku: string;
  last_synced: string;
  connection: string;
  // TikTok-specific
  product_id?: string;
  title?: string;
  sku_id?: string;
  category_chains?: string;
  main_images?: string;
  currency?: string;
  brand_name?: string;
  // Lazada-specific
  item_id?: string;
  product_name?: string;
  shop_sku?: string;
  primary_category?: string;
  images?: string;
  brand?: string;
  // Shopee-specific
  shopee_item_id?: string;
  shop_id?: string;
  category_name?: string;
  item_name?: string;
  // Outbound sync tracking (Plan 192, Epic 04)
  last_outbound_sync?: string;
  last_outbound_status?: "Success" | "Failed";
  last_stock_push_qty?: number;
  last_stock_push_at?: string;
  last_price_push_value?: number;
  last_price_push_at?: string;
}

export interface SyncActivityEntry {
  type: string;
  status: string;
  channel: string;
  direction: "inbound" | "outbound";
  records_synced: number;
  error_message?: string;
  started_at: string;
  sync_subtype?: string;
}

export interface PushResult {
  status: "ok" | "error";
  message?: string;
  stock_result?: { success: boolean; qty_pushed?: number; error?: string; message?: string };
  price_result?: { success: boolean; price_pushed?: number; error?: string; message?: string };
}

export interface ChannelListingsResponse {
  count: number;
  listings: Array<{
    name: string;
    item_code: string;
    listing_title: string;
    status: string;
    price: number;
    last_synced: string;
  }>;
}

/** Item returned by the link search API */
export interface LinkableItem {
  item_code: string;
  item_name: string;
  image: string | null;
  channels: string[];
}

// --- Product Creation (Plan 192, Epic 06) ---

const PRODUCT_CREATION_API = "zaviago_backend.storefront.product_creation";

export interface ChannelCategory {
  category_id: string;
  name: string;
  parent_id?: string;
  is_leaf?: boolean;
}

export interface CreateListingResult {
  success: boolean;
  listing_name: string;
  channel_item_id: string;
  message?: string;
}

export const listingsApi = {
  getItemListings(itemCode: string) {
    return getERPClient().call<MarketplaceListing[]>(
      `${API}.get_item_listings`,
      { item_code: itemCode },
    );
  },

  getChannelListings(channel: string, company?: string, limit = 5, page = 1, connectionName?: string) {
    return getERPClient().call<ChannelListingsResponse>(
      `${API}.get_channel_listings`,
      { channel, company, limit, page, ...(connectionName && { connection_name: connectionName }) },
    );
  },

  searchLinkableItems(query: string, excludeItemCode?: string) {
    return getERPClient().call<LinkableItem[]>(
      `${API}.search_linkable_items`,
      { query, exclude_item_code: excludeItemCode, limit: 20 },
    );
  },

  linkItem(sourceItemCode: string, targetItemCode: string) {
    return getERPClient().call<{ success: boolean; target_item_code: string }>(
      `${API}.link_item_to_listing`,
      { source_item_code: sourceItemCode, target_item_code: targetItemCode },
    );
  },

  unlinkListing(listingName: string, listingDoctype: string) {
    return getERPClient().call<{ success: boolean; new_item_code: string }>(
      `${API}.unlink_listing`,
      { listing_name: listingName, listing_doctype: listingDoctype },
    );
  },

  getItemSyncActivity(itemCode: string) {
    return getERPClient().call<SyncActivityEntry[]>(
      `${API}.get_item_sync_activity`,
      { item_code: itemCode },
    );
  },

  pushItemToChannel(itemCode: string, channel: string, listingName: string) {
    return getERPClient().call<PushResult>(
      "zaviago_backend.storefront.outbound_sync.push_item_to_channel",
      { item_code: itemCode, channel, listing_name: listingName },
    );
  },

  // --- Product Creation (Plan 192, Epic 06) ---

  getChannelCategories(channel: string, connectionName: string, query?: string) {
    return getERPClient().call<ChannelCategory[]>(
      `${PRODUCT_CREATION_API}.get_channel_categories`,
      { channel, connection_name: connectionName, query: query || "" },
    );
  },

  createChannelListing(itemCode: string, channel: string, connectionName: string, categoryId: string) {
    return getERPClient().call<CreateListingResult>(
      `${PRODUCT_CREATION_API}.create_listing_on_channel`,
      { item_code: itemCode, channel, connection_name: connectionName, category_id: categoryId },
    );
  },
};
